// ── release gates ────────────────────────────────────────────────────────────
//
// A PUBLIC surface can't be gated by `canSee()` — the person reading it is a
// stranger, and there is nobody to check. So the question for a public page is
// not "who are you" but "has this been approved to exist in public yet."
//
// That is one boolean per surface, and it has to be honoured in THREE places at
// once or the gate leaks:
//   1. the page's own `robots` metadata      — keeps it out of search
//   2. the site menu (lib/nav/siteNav.ts)    — keeps it off every page's nav
//   3. the sitemap (app/sitemap.ts)          — keeps it out of the crawl list
//
// Until a gate opens, the page still RENDERS for anyone with the direct link.
// That is deliberate: it is how the surface gets reviewed, demoed, and pasted
// into a group chat before launch. It is unlisted, not secret — so nothing
// genuinely private may live on one of these pages.
//
// Flipping a gate to `true` is the approval. It is a one-line diff on purpose:
// the whole point is that "make it public" is a single reviewable change, not a
// scavenger hunt through three files.

export interface ReleaseGate {
  /** the surface's route, for the operator reading this list. */
  route: string
  /** open? `false` = unlisted: renders on a direct link, invisible everywhere else. */
  open: boolean
  /** the release this is waiting on (lib/release/ledger.ts). */
  version: string
  /** what has to be true before this flips. Write it honestly. */
  waitingOn: string
}

export const RELEASE_GATES = {
  /**
   * `/ours` — the public door for the shared side.
   *
   * Closed until the ours campaign runs (marketing/ours/campaign.md, beat 1).
   * The rooms themselves are founder-gated and the waitlist is the whole point
   * of the page, so opening this is a marketing decision, not a technical one.
   */
  oursPublic: {
    route: '/ours',
    open: false,
    version: '1.2',
    waitingOn: 'the ours campaign beat 1 — the essay lands first, then this page is listed',
  },
  /**
   * `/community` — the public commons (skills · guides · resources).
   *
   * Built 2026-08, deployed unlisted, and opened 2026-09-14. It had reached
   * the ≡ menu, the footer row and the sitemap the moment it was first
   * committed, which is the failure this whole file exists to prevent:
   * nothing "in the works" is listed on the public site until John says it
   * is ready. He said so on 2026-09-14, and this gate is the one line that
   * carried it out.
   */
  communityPublic: {
    route: '/community',
    // OPENED 2026-09-14 on John's call: the commons goes into every ≡ menu
    // (community → skills · guides · resources) and into the footer row as
    // "skills and guides". Flipping this one line is what listed it in the
    // menu, the footer, the sitemap, llms.txt and the pages' own robots.
    open: true,
    version: '1.3',
    waitingOn: 'nothing — opened 2026-09-14 (John, in-session)',
  },
  /**
   * `/kit` — the media kit's stages (/kit/netti, /kit/frame) and the module
   * /kit/netti.js. Built 2026-10-01 for creators and their AI tools: they are
   * handed out by link (the `innernet-media-kit` skill, the creators deck), so
   * they render for anyone, but nothing lists them yet.
   */
  kitPublic: {
    route: '/kit',
    open: false,
    version: '1.3',
    waitingOn: 'John: whether the kit is listed beside the commons (menu, sitemap, llms.txt)',
  },
  /**
   * `/film` — "a home for you", the film the 1.3 letter opens on.
   *
   * Unlisted until John lists it: the letter's poster links here directly, so
   * every reader can watch it, but it stays out of search, the menu and the
   * sitemap until he says the film is the public face too.
   */
  filmPublic: {
    route: '/film',
    open: false,
    version: '1.3',
    waitingOn: 'John lists the film (after the 1.3 letter goes out)',
  },
  /**
   * `/developers` — innernet for developers: memory for the agents people
   * build (AGENTS-MEMORY-PLAN.md §8). Until 2026-10-12 the route was a
   * redirect to the api panel; it is now the developers homepage. Unlisted
   * while the agents module is new: the api is open to every key and the docs
   * say so, but the page waits for John before it is listed. It keeps the
   * mint-a-key door (the mcp and rest errors send people here).
   */
  developersPublic: {
    route: '/developers',
    open: false,
    version: '1.4',
    waitingOn: 'John: the agents module reviewed live (migration 146 applied, the agents space opened past beta)',
  },
} as const satisfies Record<string, ReleaseGate>

export type ReleaseGateKey = keyof typeof RELEASE_GATES

/** Is this surface public yet? Unknown keys fail CLOSED, like canSee(). */
export function gateOpen(key: ReleaseGateKey): boolean {
  return RELEASE_GATES[key]?.open ?? false
}

/** The `robots` metadata a gated page should carry. */
export function gateRobots(key: ReleaseGateKey) {
  return gateOpen(key) ? undefined : { index: false, follow: false }
}

// ── rooms ────────────────────────────────────────────────────────────────────
//
// Rooms (a shared memory a few people keep together: begin one, step into one
// with an invite, read it and add to it from any tool) are not a public page,
// so they are not one of the gates above and cost the sitemap nothing. They are
// a SIGNED-IN surface, and the question for one is "has it been approved for
// everyone yet", which is the same kind of one-line yes.
//
// Until John says so, rooms are for the innernet team. `ROOMS_OPEN` is the whole
// switch, and it is read in exactly one place, `roomsOpenTo()` in
// lib/rooms/open.ts, which every door asks: the ours panel on the dashboard, a
// room's page, the invite page, joining (web, REST and `npx innernet join`),
// beginning a room, the MCP and REST door, /api/rooms, /api/spaces, the people
// list and the never-cross switch. Closed, a person who is not on the team is
// told once, kindly, that rooms are open to the team for now, and everything
// else behaves as if they stood in no room at all. A failed check reads as
// closed, like canSee().
//
// Everything that tells a stranger rooms exist waits on the same line: the
// `join` command in the cli's help and readme, its rows in the docs, and the
// features registry row. The places that carry that wait say so beside the
// text, and come back when this flips.
//
// Flipping it to `true` is the approval, and it is a one-line diff on purpose.
// Beginning a room stays a founder's act until its own policy (migration 132)
// is widened, which is a separate decision.
export const ROOMS_OPEN: boolean = false

/** Said beside anything public that names rooms (the docs, the api reference) while they are for the team: empty once they open. */
export const ROOMS_FOR_NOW: string = ROOMS_OPEN ? '' : ' (open to the innernet team for now)'
